/**
 * React Hook for Dependency Manager Integration
 *
 * Provides a hook that integrates the dependency manager with React state
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { JSONSchema7 } from 'json-schema';
import isEqual from 'lodash/isEqual';
import {
  DependencyMiddleware,
  DependencyManagerConfig,
  FieldUpdate,
  BatchedUpdateManager,
  applyBatchedUpdate,
  setValueAtPath,
  DEFAULT_ID_SEPARATOR,
  validateSchema,
  defaultDependencyHandlers,
  DependencyHandler,
  FieldPath,
  getValueAtPath,
} from './index';

export interface UseDependencyManagerOptions {
  /** The JSON Schema for the form */
  schema: JSONSchema7;
  /** Current form data */
  formData: any;
  /** Callback to update form data */
  onFormDataChange: (newFormData: any) => void;
  /** Optional custom configuration */
  config?: Partial<DependencyManagerConfig>;
  /** Optional key to control when middleware is recreated (e.g., form step key) */
  middlewareKey?: string;
}

export interface UseDependencyManagerResult {
  /** Notifies the dependency manager of a field update */
  notifyUpdate: (update: FieldUpdate) => void;
  /** Registers a custom dependency handler for a specific field. Returns cleanup function. */
  registerHandler: (
    fieldPath: FieldPath,
    handler: DependencyHandler,
  ) => (() => void) | undefined;
  /** Registers dependencies between fields */
  registerDependencies: (fieldPath: string, dependsOn: string[]) => void;
  /** Whether the dependency manager is ready */
  isReady: boolean;
  /** Any validation errors from the schema */
  validationErrors: string[];
  /** Whether async updates are currently pending (can be used to disable form) */
  isLoading: boolean;
  /** Unique ID of the middleware instance */
  id?: string;
}

/**
 * Hook to integrate the Dependency Manager with a React form
 *
 * @param options - Configuration options
 * @returns Dependency manager interface
 */
export function useDependencyManager(
  options: UseDependencyManagerOptions,
): UseDependencyManagerResult {
  const {
    schema,
    formData,
    onFormDataChange,
    config: userConfig = {},
    middlewareKey, // Use this to control when middleware is recreated
  } = options;

  const config: DependencyManagerConfig = useMemo(
    () => ({
      idSeparator: DEFAULT_ID_SEPARATOR,
      debug: false,
      ...userConfig,
    }),
    [userConfig],
  );

  // Validate schema on mount
  const validationErrors = useMemo(() => {
    const result = validateSchema(schema, config);
    if (!result.valid) {
      return result.errors.map(err => `${err.path}: ${err.message}`);
    }
    return [];
  }, [schema, config]);

  // Create dependency middleware
  // Only recreate when middlewareKey changes (not on every schema reference change)
  const middlewareRef = useRef<DependencyMiddleware | null>(null);
  const previousMiddlewareKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    previousMiddlewareKeyRef.current = middlewareKey;

    const newMiddleware = new DependencyMiddleware(
      schema,
      config,
      defaultDependencyHandlers.preserve,
    );

    middlewareRef.current = newMiddleware;
  }, [middlewareKey, schema, config]);

  // Keep a ref to the latest formData
  // This ref is updated via useEffect to ensure we always have the latest formData
  // even if notifyUpdate callback was created with an older formData in its closure
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Create batched update manager
  const batchManagerRef = useRef<BatchedUpdateManager | null>(null);

  useEffect(() => {
    const handleBatchReady = (batch: any) => {
      const newFormData = applyBatchedUpdate(
        formDataRef.current, // Use ref to get latest formData
        batch,
        config.idSeparator,
      );

      // Update ref immediately so subsequent batches use the latest data
      // This is critical when multiple batches are applied in quick succession
      // (e.g., sync updates followed by async updates before React re-renders)
      formDataRef.current = newFormData;

      onFormDataChange(newFormData);
    };

    batchManagerRef.current = new BatchedUpdateManager(handleBatchReady);

    return () => {
      batchManagerRef.current?.clear();
    };
  }, [onFormDataChange, config.idSeparator]);

  // Track async update loading state
  const [isLoading, setIsLoading] = useState(false);

  // Notify update handler
  const notifyUpdate = useCallback(
    async (update: FieldUpdate) => {
      // Use formDataRef.current to get the LATEST formData
      // This is critical because this callback might have been created with an older
      // formData in its closure, but formDataRef is always up-to-date via useEffect
      const currentFormData = formDataRef.current;

      if (!middlewareRef.current || !batchManagerRef.current) {
        // Fallback to direct update when disabled
        const directUpdate = applyBatchedUpdate(
          currentFormData,
          { updates: [{ path: update.fieldPath, value: update.value }] },
          config.idSeparator,
        );
        onFormDataChange(directUpdate);
        return;
      }

      // Check if the value has actually changed
      // This prevents overwriting formData when nothing has changed (e.g., during array operations)
      // BUT we still need to propagate to dependents even if the original value is unchanged
      // (e.g., when flushSync already set the value but dependents need notification)
      const currentValue = getValueAtPath(
        currentFormData,
        update.fieldPath,
        config.idSeparator,
      );

      // Use lodash isEqual for deep comparison (more reliable than JSON.stringify)
      const valuesAreEqual = isEqual(currentValue, update.value);

      if (valuesAreEqual && update.source === 'user') {
        return;
      }
      const batchedUpdate = middlewareRef.current.processUpdate(
        update,
        currentFormData, // Use currentFormData from ref, not closure
      );

      // Queue all sync updates (already in correct topological order)
      // Skip the original field update if value is already set (to avoid double updates when flushSync was used)
      // but still queue all dependent field updates
      batchedUpdate.updates.forEach(({ path, value }) => {
        if (
          path === update.fieldPath &&
          valuesAreEqual &&
          update.source === 'user'
        ) {
          return;
        }
        batchManagerRef.current!.queueUpdate(path, value);
      });

      // Handle async updates with cascading support
      // When async handlers resolve, downstream dependents need to be re-processed
      // because they were called with stale formData during the initial cascade.
      // For example: dataproduct → dataproductObject (async) → solutionUrn → solutionObject
      // When dataproductObject resolves, solutionUrn and solutionObject must be re-triggered.
      const processAsyncCascade = (
        asyncPromises: Array<Promise<{ path: FieldPath; value: any }>>,
      ) => {
        Promise.all(asyncPromises)
          .then(resolvedUpdates => {
            // Queue all resolved values
            resolvedUpdates.forEach(({ path, value }) => {
              if (batchManagerRef.current) {
                batchManagerRef.current.queueUpdate(path, value);
              }
            });

            // Clear async updates AFTER processing them
            const asyncManager = middlewareRef.current?.getAsyncManager();
            if (asyncManager) {
              asyncManager.clear();
            }

            // Re-cascade: for each resolved async field, trigger processUpdate
            // so downstream dependents get processed with the now-available values
            if (middlewareRef.current && resolvedUpdates.length > 0) {
              // Build formData snapshot with all resolved async values applied
              const cascadeFormData = resolvedUpdates.reduce(
                (fd, u) =>
                  setValueAtPath(fd, u.path, u.value, config.idSeparator),
                formDataRef.current,
              );

              resolvedUpdates.forEach(({ path: resolvedPath, value }) => {
                const cascadeResult = middlewareRef.current!.processUpdate(
                  {
                    fieldPath: resolvedPath,
                    value,
                    source: 'user',
                    intent: 'manual',
                  },
                  cascadeFormData,
                );

                // Queue sync updates from cascade (skip the trigger field itself)
                cascadeResult.updates
                  .filter(u => u.path !== resolvedPath)
                  .forEach(u => {
                    if (batchManagerRef.current) {
                      batchManagerRef.current.queueUpdate(u.path, u.value);
                    }
                  });
              });

              // Handle nested async from the re-cascade (recursive)
              const nestedAsyncPromises =
                middlewareRef.current.getPendingAsyncUpdates();
              if (nestedAsyncPromises.length > 0) {
                processAsyncCascade(nestedAsyncPromises);
                return; // setIsLoading(false) will be called when nested cascade completes
              }
            }

            setIsLoading(false);
          })
          .catch(() => {
            // Clear async updates even on error
            const asyncManager = middlewareRef.current?.getAsyncManager();
            if (asyncManager) {
              asyncManager.clear();
            }
            setIsLoading(false);
          });
      };

      const pendingAsyncPromises =
        middlewareRef.current.getPendingAsyncUpdates();
      if (pendingAsyncPromises.length > 0) {
        setIsLoading(true);
        processAsyncCascade(pendingAsyncPromises);
      }
    },
    [onFormDataChange, config.idSeparator],
  );

  // Register custom handler
  const registerHandler = useCallback(
    (fieldPath: FieldPath, handler: DependencyHandler) => {
      if (!middlewareRef.current || !batchManagerRef.current) return undefined;

      // Register the handler with the middleware
      const [unregister, pendingValue] = middlewareRef.current.registerHandler(
        fieldPath,
        handler,
      );

      // ALWAYS check for pending value, regardless of whether newly registered or not
      // This ensures updates propagate when user changes dependency multiple times
      if (pendingValue !== undefined) {
        const pendingTracker = middlewareRef.current!.getPendingValueTracker();

        // Only process if not already processed
        if (!pendingTracker.isProcessed(fieldPath)) {
          pendingTracker.markProcessed(fieldPath);

          // Trigger handler synchronously and queue update immediately
          // Don't return yet - ensure the update is queued before we return unregister
          const handlerResult = handler(
            fieldPath,
            '', // We don't know the exact dependency path from pending value
            pendingValue,
            formDataRef.current,
            config,
          );

          // If handler returns a Promise (async), we need to handle it
          if (handlerResult instanceof Promise) {
            // Queue the update asynchronously through the batch manager
            // Don't apply immediately - collect with other updates
            handlerResult.then(resolvedValue => {
              if (batchManagerRef.current) {
                batchManagerRef.current.queueUpdate(fieldPath, resolvedValue);
              }
            });
          } else {
            // Synchronous handler - queue update immediately
            if (batchManagerRef.current) {
              batchManagerRef.current.queueUpdate(fieldPath, handlerResult);
            }
          }
        }

        return unregister;
      }

      // ALWAYS check for pending values, whether newly registered or not
      // This handles the race condition where the dependency was updated
      // AFTER the initial handler registration (important for pagination)

      // Get the COMPLETE dependency graph (schema-derived + runtime-registered)
      const graph = middlewareRef.current?.getCompleteDependencyGraph() ?? {};

      // Find all fields that have this fieldPath as a dependent
      // Note: graph contains template paths with [] for arrays, but fieldPath has actual indices
      // e.g., graph has "dataAssets>[]>physicalAsset>developmentGroup", fieldPath is "dataAssets>0>physicalAsset>developmentGroup"
      const dependencies: FieldPath[] = [];
      const escapedSeparator = config.idSeparator.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&',
      );

      for (const [sourceField, dependents] of Object.entries(graph)) {
        for (const templateDependent of dependents) {
          // Convert template path to regex pattern
          const pattern = templateDependent.replace(
            new RegExp(`${escapedSeparator}\\[\\]`, 'g'),
            `${escapedSeparator}\\d+`,
          );
          const regex = new RegExp(`^${pattern}$`);

          // Check if this fieldPath matches the template pattern
          if (regex.test(fieldPath) || templateDependent === fieldPath) {
            dependencies.push(sourceField);
            break; // Found a match for this source field
          }
        }
      }

      // Collect all late handler promises to execute in parallel
      const lateHandlerPromises: Array<Promise<void>> = [];

      // For each dependency, check if it has a value and trigger the handler
      // This runs when a handler first registers to populate fields based on existing dependency values
      // IMPORTANT: We don't use hasProcessedValue check here because this is the INITIAL population
      // The handler should always run when registered if the dependency has a value
      dependencies.forEach(depFieldPath => {
        const depValue = getValueAtPath(
          formDataRef.current,
          depFieldPath,
          config.idSeparator,
        );

        if (depValue !== undefined && depValue !== null && depValue !== '') {
          // Call the handler asynchronously and queue the update when it resolves
          // The update will be applied through the batch manager's normal flow via microtask
          const handlerPromise = Promise.resolve(
            handler(
              fieldPath,
              depFieldPath,
              depValue,
              formDataRef.current,
              config,
            ),
          ).then(resolvedValue => {
            // Queue the update - the batch manager will handle timing
            if (batchManagerRef.current) {
              batchManagerRef.current.queueUpdate(fieldPath, resolvedValue);
            }
          });

          lateHandlerPromises.push(handlerPromise);
        }
      });

      // No need to explicitly flush - the batch manager will handle it via microtask

      return unregister;
    },
    [config],
  );

  // Expose registerDependencies method
  const registerDependencies = useCallback(
    (fieldPath: string, dependsOn: string[]) => {
      if (middlewareRef.current) {
        middlewareRef.current.registerDependencies(fieldPath, dependsOn);
      }
    },
    [],
  );

  return {
    notifyUpdate,
    registerHandler,
    registerDependencies,
    isReady: middlewareRef.current !== null && validationErrors.length === 0,
    validationErrors,
    isLoading,
    id: middlewareRef.current?.getId(),
  };
}
